import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import type { StepId, TranslationFunction } from "../types";

interface AggregatorRegistrationStep {
  id: StepId;
  titleKey: string;
}

interface AggregatorRegistrationStepNavigationProps {
  steps: readonly AggregatorRegistrationStep[];
  activeStepId: StepId;
  isBusy: boolean;
  label: string;
  isStepComplete: (stepId: StepId) => boolean;
  isStepDisabled: (stepId: StepId) => boolean;
  onStepSelect: (stepId: StepId) => void | Promise<void>;
  t: TranslationFunction;
}

export const AggregatorRegistrationStepNavigation = ({
  steps,
  activeStepId,
  isBusy,
  label,
  isStepComplete,
  isStepDisabled,
  onStepSelect,
  t,
}: AggregatorRegistrationStepNavigationProps) => (
  <nav aria-label={label} className="mt-8">
    <ol>
      {steps.map((stepDefinition, index) => {
        const isActive = activeStepId === stepDefinition.id;
        const isComplete = isStepComplete(stepDefinition.id);
        const isCompletedPreviousStep = isComplete && !isActive;
        const isLastStep = index === steps.length - 1;
        const isDisabled = isStepDisabled(stepDefinition.id) || isBusy;

        return (
          <li
            key={stepDefinition.id}
            className={cn("relative pb-10 last:pb-0", isDisabled && "opacity-60")}
          >
            {!isLastStep && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute bottom-0 left-[1.125rem] top-9 w-px transition-colors",
                  isComplete ? "bg-primary" : "bg-border"
                )}
              />
            )}
            <button
              type="button"
              aria-current={isActive ? "step" : undefined}
              disabled={isDisabled}
              onClick={() => {
                void onStepSelect(stepDefinition.id);
              }}
              className={cn(
                "relative z-10 flex w-full items-center gap-3 bg-transparent text-left text-sm transition-colors",
                isDisabled ? "cursor-not-allowed" : "hover:text-primary"
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors",
                  isCompletedPreviousStep
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-primary bg-background text-primary",
                  isActive && !isCompletedPreviousStep && "bg-primary text-primary-foreground"
                )}
              >
                {isCompletedPreviousStep ? <Check className="size-4" /> : index + 1}
              </span>
              <span
                className={cn(
                  "font-semibold leading-5",
                  isActive || isCompletedPreviousStep ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {t(stepDefinition.titleKey)}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  </nav>
);

export type { AggregatorRegistrationStepNavigationProps };
